import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];

const Shell = ({ children }) => (
  <main data-testid="payment-page" className="min-h-screen bg-cream text-ink flex flex-col">
    <div className="grain-fixed" aria-hidden />
    <header className="mx-auto w-full max-w-[1400px] px-5 sm:px-8 h-20 flex items-center">
      <Link to="/" data-testid="payment-home-link" className="font-serif text-ocean text-xl tracking-tight leading-none">
        Your Business Roadmap
        <span className="block text-[10px] font-sans uppercase tracking-[0.28em] text-cognac mt-0.5">in 7 days · with Brian Smith</span>
      </Link>
    </header>
    <section className="flex-1 flex items-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mx-auto w-full max-w-[1400px] px-5 sm:px-8 py-16 grid lg:grid-cols-12 gap-10"
      >
        {children}
      </motion.div>
    </section>
  </main>
);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) return setState("error");
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        if (!res.ok) throw new Error("not found");
        const d = await res.json();
        if (["paid", "card_saved"].includes(d.payment_status)) {
          setData(d);
          return setState("done");
        }
        if (["failed", "expired"].includes(d.payment_status)) {
          setData(d);
          return setState("failed");
        }
      } catch (e) {
        if (attempts >= 3) return setState("error");
      }
      if (attempts >= 8) return setState("pending");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const r = data?.reservation;
  const first = r?.name?.split(" ")[0];
  const later = data?.payment_status === "card_saved";

  return (
    <Shell>
      <div className="lg:col-span-7">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">One moment</span>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-ocean tracking-tight leading-[1.02]">Confirming your reservation…</h1>
          </div>
        )}
        {state === "done" && (
          <div data-testid="payment-success">
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">
              {later ? "Card saved · Seat held" : "Payment complete · Seat confirmed"}
            </span>
            <h1 className="mt-5 font-serif text-4xl sm:text-6xl text-ocean tracking-[-0.02em] leading-[0.98]">
              Welcome, {first}.<br />
              <span className="italic text-cognac">
                {r?.founding_50 ? `You're founder #${r.position} of the Founding 50.` : `You're #${r?.position} on the list.`}
              </span>
            </h1>
            <p data-testid="payment-success-detail" className="mt-7 max-w-xl font-sans text-base sm:text-lg text-ink/72 leading-relaxed">
              {later
                ? "Nothing has been charged today. Your card is securely on file with Stripe and will be billed $447 on January 1, 2027 — your founding price is locked in."
                : "Your $397 founding payment is complete and your seat is secured at the founding price."}
            </p>
            <p className="mt-4 max-w-xl font-sans text-base text-ink/72 leading-relaxed">
              {r?.founding_50
                ? "Watch your inbox for the private live kickoff details with Brian Smith on January 5, 2027."
                : "The 50 live-kickoff seats are taken, but your place in the program is secured for the January 5, 2027 launch."}
            </p>
          </div>
        )}
        {(state === "pending" || state === "error" || state === "failed") && (
          <div data-testid="payment-unresolved">
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">Still checking</span>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-ocean tracking-tight leading-[1.02]">
              {state === "failed" ? "That payment didn't go through." : "We're still confirming with Stripe."}
            </h1>
            <p className="mt-6 max-w-xl font-sans text-base text-ink/72 leading-relaxed">
              {state === "failed"
                ? "No seat was taken and nothing was charged. You can head back and try again."
                : "If you completed checkout, your seat is safe — confirmation may take a minute. Refresh this page or check your email."}
            </p>
          </div>
        )}
        <Link to="/" data-testid="payment-back-home" className="mt-10 inline-block link-underline font-sans text-sm uppercase tracking-[0.14em] text-ocean">
          ← Back to the roadmap
        </Link>
      </div>
      <div className="lg:col-span-5">
        <figure className="relative grain overflow-hidden border border-[var(--line)]">
          <img src="/images/brian-bookshelf.png" alt="Brian Smith" className="w-full h-[48vh] object-cover object-top img-warm" />
        </figure>
      </div>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div className="lg:col-span-7" data-testid="payment-cancel">
        <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">Checkout cancelled</span>
        <h1 className="mt-5 font-serif text-4xl sm:text-6xl text-ocean tracking-[-0.02em] leading-[0.98]">
          No charge was made.<br /><span className="italic text-cognac">Your seat isn't held yet.</span>
        </h1>
        <p className="mt-7 max-w-xl font-sans text-base sm:text-lg text-ink/72 leading-relaxed">
          Whenever you're ready, head back and reserve your Founding 50 spot — pay $397 now, or save your card and pay $447 on January 1, 2027.
        </p>
        <Link to="/#founding" data-testid="payment-cancel-back" className="mt-10 inline-block font-sans text-sm uppercase tracking-[0.18em] bg-ocean text-cream px-8 py-4 hover:bg-cognac transition-colors">
          Back to the roadmap →
        </Link>
      </div>
      <div className="lg:col-span-5">
        <figure className="relative grain overflow-hidden border border-[var(--line)]">
          <img src="/images/brian-boots-desk.png" alt="" className="w-full h-[48vh] object-cover img-warm" />
        </figure>
      </div>
    </Shell>
  );
}
